import { useCallback, useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Icon } from "@core/icons";
import { ApiError, type Problem } from "@core/http";
import type { components } from "@core/types/openapi";
import { Shell } from "../access/AccessLayout";
import { BigButton } from "../access/primitives";
import { clearValidatorSession, loadValidatorSession } from "../access/session";
import { signOut } from "../access/api";
import type { CodeToValidate } from "../home/api";
import { SessionHeader } from "../home/SessionHeader";
import { confirmRedemption, type RedemptionReceipt } from "./api";
import "./result.css";

// SPEC-304 · Consumo y resultado — `validador.jsx:464-540` (`Resultado`). Una
// sola pantalla, ocho desenlaces (R-02 del brief): éxito + siete condiciones
// de error del contrato + sin conexión, que no tiene respuesta de servidor.
//
// PROTO-02 (resolución humana) retira la tarjeta de identificación del
// miembro de los ocho desenlaces de error: el avatar lo retira ANNEX-008
// (mismo trato que PA-041 en SPEC-303) y el resto — `ValidatorProblem` no
// trae ningún dato del miembro — no existe en el contrato HTTP del rechazo.
// El bloque «Qué decirle al miembro» se dibuja en las ocho condiciones de
// error: en `QR_EXPIRED` con el literal estático del prototipo (`say` +
// `expired-instruction`, canónicos en el contrato visual); en las otras
// siete con `ValidatorProblem.counterPhrase`, required en todo rechazo.
const OFFLINE_MESSAGES = {
  // Literales propios (R-04): sin respuesta de servidor que mostrar. Calcado
  // de `validador.jsx:63` (`ERRORES.offline`), traducido al tuteo (R-05 / T7,
  // `userMessageHygiene.test.ts`).
  title: "Sin conexión",
  detail:
    "No pudimos confirmar la validación. Revisa la señal o el wifi del local; el consumo no se registró.",
  counterPhrase:
    "No se pudo confirmar el canje por la conexión; vuelve a intentarlo en un momento.",
};

type IconName = "clock" | "x" | "store" | "alert" | "wifi";

const ICON_BY_CODE: Record<string, IconName> = {
  QR_EXPIRED: "clock",
  QR_USED: "x",
  MERCHANT_NOT_ENABLED: "store",
  MEMBER_INACTIVE: "alert",
  REDEMPTION_EXHAUSTED: "clock",
  INSUFFICIENT_BALANCE: "alert",
  QR_INVALID: "x",
  NO_CONNECTION: "wifi",
};

// R-04: `action` decide el rótulo del botón primario. Los rechazos del
// backend de esta corrida siempre traen `scan_another`
// (`api/src/infrastructure/http/problem.ts`, `sendValidatorProblem`); `retry`
// es el literal propio del desenlace sin conexión.
const ACTION_LABEL: Record<string, string> = {
  scan_another: "Escanear otro",
  retry: "Reintentar",
  back_home: "Volver al inicio",
};

// WARN 3 (ola 1, code-reviewer): el rechazo del validador siempre trae
// `ValidatorProblem` (`50-validator.yaml:301-345`) — `counterPhrase`, `action`,
// `availableFrom`, `enabledPlaces`, `starCirclePhone` ya están tipados ahí, en
// vez de cuatro casts anónimos inline.
type ValidatorProblem = components["schemas"]["ValidatorProblem"];

interface LocationState {
  code?: CodeToValidate;
  entry?: "manual" | "scan";
  idempotencyKey?: string;
  problem?: Problem;
}

// Forma común para dibujar los ocho desenlaces de error con el mismo bloque:
// los siete que trae el servidor (`ValidatorProblem`) y el desenlace sin
// conexión, armado en el cliente porque no hubo respuesta que traducir.
interface DisplayProblem {
  code?: string;
  title: string;
  detail?: string | null;
  counterPhrase: string;
  action: string;
  starCirclePhone?: string | null;
  enabledPlaces?: ReadonlyArray<{ name?: string }> | null;
}

type Outcome =
  | { kind: "loading" }
  | { kind: "success"; receipt: RedemptionReceipt }
  | { kind: "error"; problem: DisplayProblem }
  | { kind: "offline" };

const ECUADOR_UTC_OFFSET_MS = 5 * 60 * 60 * 1000;
const MONTHS = [
  "ene",
  "feb",
  "mar",
  "abr",
  "may",
  "jun",
  "jul",
  "ago",
  "sep",
  "oct",
  "nov",
  "dic",
];

// SPEC-012 §4 / `validador.jsx:482`: fecha y hora en hora de Ecuador, "08 ago
// 2026 · 12:06" — mismo criterio de conversión que
// `api/src/infrastructure/http/problem.ts` (`ecuadorDayMonthTime`).
function formatOccurredAt(iso: string): string {
  const instant = new Date(new Date(iso).getTime() - ECUADOR_UTC_OFFSET_MS);
  const day = String(instant.getUTCDate()).padStart(2, "0");
  const month = MONTHS[instant.getUTCMonth()];
  const year = instant.getUTCFullYear();
  const hours = String(instant.getUTCHours()).padStart(2, "0");
  const minutes = String(instant.getUTCMinutes()).padStart(2, "0");
  return `${day} ${month} ${String(year)} · ${hours}:${minutes}`;
}

function toDisplayProblem(problem: Problem): DisplayProblem {
  // El contrato del validador siempre responde `ValidatorProblem` en 400/409/422
  // (`50-validator.yaml:301-345`); `Problem` es el tipo genérico de `@core/http`.
  const validatorProblem = problem as ValidatorProblem;
  return {
    code: problem.code,
    title: problem.title ?? "No se pudo confirmar el canje",
    detail: problem.detail ?? null,
    counterPhrase:
      validatorProblem.counterPhrase ??
      "Comunícate con Star Circle para resolverlo.",
    action: validatorProblem.action ?? "scan_another",
    starCirclePhone: validatorProblem.starCirclePhone ?? null,
    enabledPlaces: validatorProblem.enabledPlaces ?? null,
  };
}

export function ResultScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as LocationState | null) ?? null;
  const code = state?.code ?? null;
  const idempotencyKey = state?.idempotencyKey;
  const initialProblem = state?.problem ?? null;
  // `RequireValidatorSession` (App.tsx) ya garantiza sesión válida antes de
  // montar esta pantalla; mismo trato defensivo que `HomeScreen`/`ConfirmScreen`.
  const session = loadValidatorSession();
  const [signingOut, setSigningOut] = useState(false);
  const [outcome, setOutcome] = useState<Outcome>(() =>
    initialProblem
      ? { kind: "error", problem: toDisplayProblem(initialProblem) }
      : { kind: "loading" },
  );

  const bearerToken = session?.token ?? "";

  const runConfirm = useCallback(() => {
    if (!code || !idempotencyKey) return;
    setOutcome({ kind: "loading" });
    confirmRedemption(code.publicCode, idempotencyKey, bearerToken)
      .then((receipt) => {
        setOutcome({ kind: "success", receipt });
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.problem) {
          setOutcome({ kind: "error", problem: toDisplayProblem(err.problem) });
          return;
        }
        // Sin `ApiError` (el `fetch` mismo falló): corte de conexión
        // (RF-VAL-34) — el consumo no se registró, se ofrece reintentar con
        // la misma `Idempotency-Key`.
        setOutcome({ kind: "offline" });
      });
  }, [code, idempotencyKey, bearerToken]);

  useEffect(() => {
    // R-03: `state.problem` sin `state.code` es un rechazo ya resuelto por el
    // lookup — no se llama al POST, se dibuja el desenlace directamente.
    if (initialProblem) return;
    runConfirm();
  }, [initialProblem, runConfirm]);

  async function handleSignOut() {
    setSigningOut(true);
    try {
      if (session?.token) {
        await signOut(session.token);
      }
    } catch {
      // RF-VAL-11 / ANNEX-011 §2.3: la salida es local de todos modos.
    } finally {
      clearValidatorSession();
      navigate("/validator/access-login", { replace: true });
    }
  }

  function handlePrimary(action: string) {
    if (action === "retry") {
      runConfirm();
      return;
    }
    if (action === "back_home") {
      navigate("/validator/home");
      return;
    }
    navigate("/validator/scan");
  }

  // R-03: sin `code` ni `problem` (entrada directa a la ruta), redirige al
  // inicio — mismo comportamiento que `ConfirmScreen`.
  if (!code && !initialProblem) {
    return <Navigate to="/validator/home" replace />;
  }

  if (!session) {
    return null;
  }

  return (
    <Shell>
      <SessionHeader
        session={session}
        signingOut={signingOut}
        onSignOut={() => {
          void handleSignOut();
        }}
      />
      {outcome.kind === "loading" ? (
        <div className="sc-result-body">
          <div className="sc-result-loading">Confirmando canje…</div>
        </div>
      ) : outcome.kind === "success" ? (
        <SuccessBody receipt={outcome.receipt} />
      ) : (
        <ErrorBody
          problem={
            outcome.kind === "offline"
              ? {
                  code: "NO_CONNECTION",
                  title: OFFLINE_MESSAGES.title,
                  detail: OFFLINE_MESSAGES.detail,
                  counterPhrase: OFFLINE_MESSAGES.counterPhrase,
                  action: "retry",
                }
              : outcome.problem
          }
        />
      )}
      <div className="sc-result-actions">
        {outcome.kind === "success" ? (
          <>
            <BigButton
              tone="green"
              onClick={() => {
                navigate("/validator/scan");
              }}
            >
              <Icon name="qr" size={18} stroke={2} />
              Escanear otro
            </BigButton>
            <div className="sc-result-gap" />
            <BigButton
              tone="ghost"
              onClick={() => {
                navigate("/validator/home");
              }}
            >
              Volver al inicio
            </BigButton>
          </>
        ) : outcome.kind === "loading" ? null : (
          <>
            <BigButton
              tone="green"
              onClick={() => {
                const action =
                  outcome.kind === "offline" ? "retry" : outcome.problem.action;
                handlePrimary(action);
              }}
            >
              <Icon
                name={outcome.kind === "offline" ? "refresh" : "qr"}
                size={18}
                stroke={2}
              />
              {ACTION_LABEL[
                outcome.kind === "offline" ? "retry" : outcome.problem.action
              ] ?? "Escanear otro"}
            </BigButton>
            <div className="sc-result-gap" />
            <BigButton
              tone="ghost"
              onClick={() => {
                navigate("/validator/home");
              }}
            >
              Volver al inicio
            </BigButton>
          </>
        )}
      </div>
    </Shell>
  );
}

function SuccessBody({ receipt }: { receipt: RedemptionReceipt }) {
  const rows: [string, string][] = [
    ["Miembro", receipt.member],
    ["Consumido", receipt.item],
    ["Hora", formatOccurredAt(receipt.occurredAt)],
    ["Comercio", receipt.place],
    ["Código", receipt.publicCode],
  ];
  return (
    <div className="sc-result-body">
      <div className="sc-result-icon sc-result-icon--success">
        <Icon name="check" size={44} color="var(--gold-2)" stroke={2.2} />
      </div>
      <h1 className="sc-result-title">Consumo confirmado</h1>
      {/* "puedes", no "puede": T7/QA-MTZ-002 §5.4 exige tuteo único, sin
          "usted" — el prototipo (`validador.jsx:479`) usa la forma de
          "usted" ("Ya puede entregar el beneficio al miembro."). */}
      <p className="sc-result-copy">
        Ya puedes entregar el beneficio al miembro.
      </p>
      <div className="sc-result-receipt">
        {rows.map(([label, value]) => (
          <div key={label} className="sc-result-receipt-row">
            <span className="sc-result-receipt-label">{label}</span>
            <span className="sc-result-receipt-value">{value}</span>
          </div>
        ))}
        {receipt.points ? (
          <div className="sc-result-receipt-row sc-result-receipt-row--points">
            <span className="sc-result-receipt-label">Puntos descontados</span>
            <span className="sc-result-points-value">
              −{(receipt.points.deducted ?? 0).toLocaleString("es-EC")} · saldo{" "}
              {(receipt.points.resultingBalance ?? 0).toLocaleString("es-EC")}
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function ErrorBody({ problem }: { problem: DisplayProblem }) {
  const icon = ICON_BY_CODE[problem.code ?? ""] ?? "alert";
  const isExpired = problem.code === "QR_EXPIRED";
  return (
    <div className="sc-result-body">
      <div className="sc-result-icon sc-result-icon--danger">
        <Icon name={icon} size={44} color="var(--danger)" stroke={1.9} />
      </div>
      <h1 className="sc-result-title">{problem.title}</h1>
      {/* WARN 2 (ola 1, spec-verifier): en MERCHANT_NOT_ENABLED, `detail` ya
          trae la prosa "Puede consumirse en: X." armada por el servidor
          (`api/src/infrastructure/http/problem.ts`, `merchantNotEnabledDetail`)
          a partir de los mismos nombres que `enabledPlaces`. Mostrar los dos
          duplica el mensaje. `enabledPlaces` es el dato estructurado del
          contrato: cuando viene poblado, es la fuente que se dibuja y se omite
          el `detail` crudo; el resto de condiciones (sin `enabledPlaces`) sigue
          mostrando `detail` normalmente. */}
      {problem.detail &&
      !(problem.enabledPlaces && problem.enabledPlaces.length > 0) ? (
        <p className="sc-result-copy">{problem.detail}</p>
      ) : null}
      {problem.starCirclePhone ? (
        <p className="sc-result-copy">
          Comunícate al {problem.starCirclePhone}.
        </p>
      ) : null}
      {problem.enabledPlaces && problem.enabledPlaces.length > 0 ? (
        <p className="sc-result-copy">
          Puede consumirse en:{" "}
          {problem.enabledPlaces.map((place) => place.name).join(", ")}.
        </p>
      ) : null}

      {/* PROTO-02: `expired` conserva su bloque canónico (`validator.result.say`
          + `validator.result.expired-instruction`, `validador.jsx:526-531`,
          ya en tuteo); las otras siete condiciones usan el `counterPhrase`
          dinámico del servidor (`validator.result.counter-phrase`). */}
      <div className="sc-result-say">
        <div className="sc-result-say-title">Qué decirle al miembro</div>
        <div className="sc-result-say-copy">
          {isExpired ? (
            <>
              «Abre de nuevo el beneficio en la app y toca <b>Generar código</b>
              ; el nuevo QR sirve por 5 minutos.»
            </>
          ) : (
            `«${problem.counterPhrase}»`
          )}
        </div>
      </div>
    </div>
  );
}
